import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Wine, AlertTriangle, CheckCircle, Heart, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function AlcoholAwarenessGuide() {
  const navigate = useNavigate();

  const healthFacts = [
    { stat: "Disrupts deep sleep", desc: "Even 1-2 drinks reduce REM sleep quality" },
    { stat: "Empty calories", desc: "A glass of wine = 125-150 calories" },
    { stat: "Anxiety increase", desc: "Alcohol actually increases anxiety 24-48 hours later" },
    { stat: "Skin aging", desc: "Dehydrates skin, causes inflammation, accelerates wrinkles" },
    { stat: "Hormone disruption", desc: "Affects estrogen levels and metabolism" }
  ];

  const alternatives = [
    { drink: "Sparkling water + lime", benefit: "Fizz, glass in hand, zero calories" },
    { drink: "Mocktail", benefit: "Fancy, social, no alcohol" },
    { drink: "Kombucha", benefit: "Probiotic benefits, slight tang" },
    { drink: "Herbal tea", benefit: "Calming, warm, ritual" },
    { drink: "Flavored seltzer", benefit: "Variety of flavors, refreshing" }
  ];

  const strategies = [
    "Track every drink this week in a journal",
    "Set a 2-drink maximum for social events",
    "Alternate alcoholic drinks with water",
    "Find your triggers: stress, boredom, social pressure?",
    "Have your alternative drink ready when craving hits"
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold mb-4">
            Week 9 • Day 1 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Alcohol Awareness
          </h1>
          <p className="text-xl text-secondary-600">Understanding Your Relationship with Alcohol</p>
        </div>

        <section className="bg-gradient-to-r from-purple-500 to-violet-500 rounded-2xl p-8 text-white mb-8">
          <div className="flex items-start gap-4">
            <Wine className="w-12 h-12 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-2xl mb-2">This Week's Goal</h3>
              <p className="text-purple-100 text-lg">
                Honestly assess your drinking habits. Track every drink. Notice patterns and triggers. 
                This isn't about judgment—it's about awareness and intentional choice.
              </p>
            </div>
          </div>
        </section>

        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6 flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-amber-500" />
            How Alcohol Affects Your Body
          </h2>
          <div className="space-y-3">
            {healthFacts.map((item, index) => (
              <div key={index} className="p-4 bg-amber-50 rounded-xl">
                <p className="font-bold text-amber-700 mb-1">{item.stat}</p>
                <p className="text-sm text-secondary-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-2xl p-8 border-2 border-purple-200 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">Healthier Alternatives</h2>
          <div className="space-y-3">
            {alternatives.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-4 flex items-center gap-4">
                <Heart className="w-5 h-5 text-purple-500 flex-shrink-0" />
                <div>
                  <span className="font-bold text-purple-700">{item.drink}</span>
                  <span className="text-secondary-600 ml-2">— {item.benefit}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Reduction Strategies</h2>
          <ul className="space-y-3">
            {strategies.map((strategy, i) => (
              <li key={i} className="flex items-center gap-2 p-3 bg-purple-50 rounded-lg text-secondary-700">
                <CheckCircle className="w-4 h-4 text-purple-500 flex-shrink-0" />
                {strategy}
              </li>
            ))}
          </ul>
        </section>
      </main>
    </div>
  );
}

export default AlcoholAwarenessGuide;
